import React, { useCallback, useMemo, useState } from 'react';
import {
  ActionCard,
  AppButton,
  Box,
  PlanActiveCheckedIcon,
  PlanActiveIcon,
  SiblingBoxesWithSpacing,
  Text,
} from '@common-fe/common-fe';
import _ from 'lodash';

import Permissions from '@/common/permissions';
import { Access } from '@/modules/core/components';

import { useSubmitFormStore } from '../../stores';

const HP_TEMPLATE_TEXT = `Updating of Health Plan Templates will affect only future created health plans created from this template.
  Updates will not reflect in any draft or active health plans previously created from this template.`;

const HP_TEXT =
  'Updating of Health Plans will affect only future health plan enrollments. Current health plan policies and deductible rules will not be changed.';

interface Props {
  onSave: () => void;
  saveButtonRef?: React.RefObject<HTMLButtonElement | null>;
  errorMessages: string[];
  successMessages: string[];
  pendingMessages: string[];
  isTemplateMode?: boolean;
  viewMode?: boolean;
}

const SaveActiveHealthPlan: React.FC<Props> = React.memo(
  ({ onSave, viewMode, errorMessages, successMessages, pendingMessages, saveButtonRef, isTemplateMode }) => {
    const [visiblePopup, setVisiblePopup] = useState(false);

    const updateValidate = useSubmitFormStore((state) => state.activateValidate);
    const handleToggleVisiblePopup = useCallback(() => {
      setVisiblePopup(!visiblePopup);
    }, [visiblePopup]);
    const handleSave = useCallback(() => {
      onSave();
      handleToggleVisiblePopup();
    }, [handleToggleVisiblePopup, onSave]);

    const CardIcon = useMemo(() => {
      if (_.isEmpty(errorMessages) && !_.isEmpty(successMessages) && updateValidate) {
        return <PlanActiveCheckedIcon size="xxlarge" color="iconSecondary" />;
      }
      return <PlanActiveIcon size="xxlarge" color="iconSecondary" />;
    }, [updateValidate, errorMessages, successMessages]);

    return (
      <Access
        accessRequirements={[
          {
            permission: Permissions.EDIT_DRAFT_PLAN,
          },
        ]}
      >
        <ActionCard
          // footer={viewMode ? <SwitchToEditMode isTemplateMode={isTemplateMode} /> : null}
          submitted={updateValidate}
          title={isTemplateMode ? 'Save the Health Plan Template' : 'Save the Health Plan'}
          modulesTitle="Required attributes"
          errorMessages={viewMode ? [] : errorMessages}
          successMessages={viewMode ? [] : successMessages}
          pendingMessages={viewMode ? [] : pendingMessages}
          icon={CardIcon}
          text={isTemplateMode ? HP_TEMPLATE_TEXT : HP_TEXT}
          minHeight="auto"
          titleWidth={isTemplateMode ? '350px' : 'auto'}
        >
          <Box
            direction="row"
            align="center"
            flex="grow"
            pad={{
              left: 'spacing8',
              right: 'spacing8',
            }}
          >
            <Box flex="grow" direction="row" justify="stretch" align="stretch">
              <SiblingBoxesWithSpacing fill>
                <AppButton disabled={viewMode} onClick={handleSave} ref={saveButtonRef} size="L" width="100%">
                  <Text color={viewMode ? 'textDisabled' : 'textOnColor'}>Save Changes</Text>
                </AppButton>
              </SiblingBoxesWithSpacing>
            </Box>
          </Box>
        </ActionCard>
      </Access>
    );
  }
);

export default SaveActiveHealthPlan;
